// ------------------------------
// components/ReceiveOnchain.tsx
// Receive on-chain payment screen
// ------------------------------

import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Text,
  Center,
  Stack,
  Title,
  Group,
  Button,
  Box,
  CopyButton,
  ActionIcon,
  Paper,
  Collapse,
  Textarea,
} from "@mantine/core";
import {
  IconCoinBitcoinFilled,
  IconCopy,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";

import { BobsWorkspace } from "../stores/BobsWorkspace";
import { OurOnchainInboundTransaction } from "../helpers/onchainInboundTransactions";

const ReceiveOnchain = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const { selectedBob } = bobsWorkspace;
    const [expandedAddresses, setExpandedAddresses] = useState<Set<string>>(
      new Set(),
    );

    const inboundTransactions: OurOnchainInboundTransaction[] =
      selectedBob.ourOnchainInboundTransactions.data;

    const getTransactionsForAddress = (address: string) => {
      return inboundTransactions.filter(
        (tx) => tx.receiving_address === address,
      );
    };

    const truncateTxId = (txId: string) => {
      if (txId.length <= 16) return txId;
      return `${txId.slice(0, 8)}...${txId.slice(-8)}`;
    };

    const formatSatoshis = (sats: number) => {
      return sats.toLocaleString();
    };

    const toggleAddress = (address: string) => {
      setExpandedAddresses((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(address)) {
          newSet.delete(address);
        } else {
          newSet.add(address);
        }
        return newSet;
      });
    };

    const truncateAddress = (address: string) => {
      if (address.length <= 20) return address;
      return `${address.slice(0, 10)}...${address.slice(-10)}`;
    };

    const addresses = selectedBob.ourOnchainAddresses.data;

    return (
      <Stack>
        <Center>
          <Group gap="xs">
            <IconCoinBitcoinFilled size={28} />
            <Title order={3}>Receive On-Chain</Title>
          </Group>
        </Center>

        <Center>
          <Text size="sm" c="dimmed">
            Receiving Bitcoin on-chain can be slow. Use Lightning for faster
            deposits.
          </Text>
        </Center>

        <Center>
          <Button
            onClick={() => selectedBob.makeNewOnchainAddress()}
            disabled={selectedBob.areWeWaitingOnRails}
            loading={selectedBob.areWeWaitingOnRails}
          >
            Generate New Address
          </Button>
        </Center>

        {addresses.length > 0 && (
          <Stack gap="sm" mt="md">
            <Box>
              <Text size="sm" fw={500} c="dimmed">
                Your Addresses ({addresses.length})
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                For better privacy, generate a new address every time you
                receive on-chain.
              </Text>
            </Box>
            {addresses.map((addr) => {
              const isExpanded = expandedAddresses.has(addr.address);
              return (
                <Paper
                  key={addr.address}
                  p="sm"
                  withBorder
                  radius="md"
                  style={{ width: "100%" }}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          flex: 1,
                        }}
                      >
                        {truncateAddress(addr.address)}
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        <CopyButton value={addr.address}>
                          {({ copied, copy }) => (
                            <ActionIcon
                              size="sm"
                              variant={copied ? "filled" : "default"}
                              color={copied ? "teal" : "blue"}
                              onClick={copy}
                              aria-label="copy address"
                            >
                              {copied ? (
                                <IconCheck size={16} />
                              ) : (
                                <IconCopy size={16} />
                              )}
                            </ActionIcon>
                          )}
                        </CopyButton>
                        <ActionIcon
                          size="sm"
                          variant="default"
                          onClick={() => toggleAddress(addr.address)}
                          aria-label="toggle address"
                        >
                          {isExpanded ? (
                            <IconChevronUp size={16} />
                          ) : (
                            <IconChevronDown size={16} />
                          )}
                        </ActionIcon>
                      </Group>
                    </Group>

                    <Collapse in={isExpanded}>
                      <Textarea
                        value={addr.address}
                        readOnly
                        autosize
                        minRows={2}
                        size="xs"
                        styles={{
                          input: {
                            fontFamily: "monospace",
                            fontSize: "11px",
                          },
                        }}
                      />
                    </Collapse>

                    <Text size="xs" c="dimmed">
                      Created{" "}
                      {formatDistanceToNow(new Date(addr.createdAt), {
                        addSuffix: true,
                      })}
                    </Text>

                    {/* Inbound Transactions for this address */}
                    {getTransactionsForAddress(addr.address).length > 0 && (
                      <Stack gap="xs" mt="xs">
                        <Text size="xs" fw={500}>
                          Inbound Transactions
                        </Text>
                        {getTransactionsForAddress(addr.address).map((tx) => (
                          <Paper
                            key={tx.outpoint}
                            p="xs"
                            withBorder
                            radius="sm"
                          >
                            <Stack gap={4}>
                              <Group justify="space-between" wrap="nowrap">
                                <Text size="xs" fw={600}>
                                  {formatSatoshis(tx.satoshis_amount)} sats
                                </Text>
                                <Text
                                  size="xs"
                                  c={
                                    tx.is_considered_confirmed
                                      ? "green"
                                      : "orange"
                                  }
                                  fw={500}
                                >
                                  {tx.is_considered_confirmed
                                    ? "Confirmed"
                                    : "Unconfirmed"}
                                </Text>
                              </Group>
                              <Group gap="xs" wrap="nowrap">
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  style={{ fontFamily: "monospace" }}
                                >
                                  {truncateTxId(tx.transaction_identifier)}
                                </Text>
                                <CopyButton value={tx.transaction_identifier}>
                                  {({ copied, copy }) => (
                                    <ActionIcon
                                      size="xs"
                                      variant={copied ? "filled" : "subtle"}
                                      color={copied ? "teal" : "gray"}
                                      onClick={copy}
                                      aria-label="copy transaction id"
                                    >
                                      {copied ? (
                                        <IconCheck size={12} />
                                      ) : (
                                        <IconCopy size={12} />
                                      )}
                                    </ActionIcon>
                                  )}
                                </CopyButton>
                              </Group>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  },
);

export default ReceiveOnchain;
